import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Toast from "./Toast";
import ConfirmDialog from "./ConfirmDialog";
import SectionLoader from "./SectionLoader";
import { getDisplayRoleName } from "../utils/roleUtils";

const permissionTypes = [
  { key: "mview", label: "View" },
  { key: "madd", label: "Add" },
  { key: "medit", label: "Edit" },
  { key: "mdelete", label: "Delete" }
];

const RolesPermissionsTab = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  // Roles CRUD state
  const [roleSearch, setRoleSearch] = useState("");
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormValue, setRoleFormValue] = useState("");
  const [roleId, setRoleId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: null });

  const API_URL = process.env.REACT_APP_API_URL || "http://45.194.3.128:4000/api/admin/";

  const fetchRolesAndPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}roles-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setRoles(Array.isArray(data.roles) ? data.roles : []);
      setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
    } catch (err) {
      console.error("Error loading roles & permissions:", err);
      setToast({ open: true, message: "Failed to load roles and permissions", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchRolesAndPermissions();
  }, [fetchRolesAndPermissions]);

  const handleRoleChange = async (e) => {
    const rId = e.target.value;
    setSelectedRole(rId);
    if (!rId) {
      setRolePermissions({});
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}user-permissions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: rId }),
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === "Invalid token") {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
      }

      let formatted = {};
      if (data.data && data.data.length > 0) {
        data.data.forEach((perm) => {
          formatted[perm.module_id] = {
            mview: perm.mview === "yes",
            madd: perm.madd === "yes",
            medit: perm.medit === "yes",
            mdelete: perm.mdelete === "yes",
            module: perm.module,
          };
        });
      } else {
        const defaultPerms = {};
        permissions.forEach((perm) => {
          defaultPerms[perm.id] = {
            mview: false,
            madd: false,
            medit: false,
            mdelete: false,
            module: perm.module,
          };
        });
        formatted = defaultPerms;
      }
      setRolePermissions(formatted);
    } catch (err) {
      setToast({ open: true, message: "Failed to load permissions for role", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (permissionId, type, module) => (event) => {
    setRolePermissions((prev) => ({
      ...prev,
      [permissionId]: {
        ...prev[permissionId],
        [type]: event.target.checked,
        module: module,
      },
    }));
  };

  const handleSaveMatrix = async () => {
    if (!selectedRole) {
      setToast({ open: true, message: "Please select a role first", severity: "warning" });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const formattedPermissions = Object.keys(rolePermissions).map((permissionId) => ({
        role_id: selectedRole,
        permission_id: parseInt(permissionId),
        mview: rolePermissions[permissionId]?.mview ? "yes" : "no",
        madd: rolePermissions[permissionId]?.madd ? "yes" : "no",
        medit: rolePermissions[permissionId]?.medit ? "yes" : "no",
        mdelete: rolePermissions[permissionId]?.mdelete ? "yes" : "no",
        module: rolePermissions[permissionId]?.module,
      }));

      const response = await fetch(`${API_URL}save-role-permissions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role_id: selectedRole, permissions: formattedPermissions }),
      });

      const result = await response.json();

      if (!response.ok || result.success === false) {
        setToast({ open: true, message: result.message || "Failed to save permissions", severity: "error" });
      } else {
        setToast({ open: true, message: "Permissions matrix updated successfully!", severity: "success" });
      }
    } catch (err) {
      setToast({ open: true, message: "Network error saving permissions", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Role CRUD actions
  const handleOpenRoleModal = (r = null) => {
    setEditingRole(r);
    setRoleId(r ? r.id : null);
    setRoleFormValue(r ? r.role : "");
    setRoleDialogOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleDialogOpen(false);
    setEditingRole(null);
    setRoleFormValue("");
  };

  const handleSubmitRole = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    if (editingRole) {
      try {
        const response = await fetch(`${API_URL}update-role`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: roleFormValue.trim(),
            roleId: roleId,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          setToast({ open: true, message: data.message || "Failed to update role", severity: "error" });
        } else {
          setToast({ open: true, message: "Role updated successfully", severity: "success" });
          fetchRolesAndPermissions();
          handleCloseRoleModal();
        }
      } catch (err) {
        setToast({ open: true, message: "Network error", severity: "error" });
      }
    } else {
      try {
        const response = await fetch(`${API_URL}add-role`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: roleFormValue.trim(),
          }),
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          setToast({ open: true, message: data.message || "Failed to add role", severity: "error" });
        } else {
          setToast({ open: true, message: "Role created successfully", severity: "success" });
          fetchRolesAndPermissions();
          handleCloseRoleModal();
        }
      } catch (err) {
        setToast({ open: true, message: "Network error", severity: "error" });
      }
    }
  };

  const handleDeleteRole = (id) => {
    setConfirmDialog({
      open: true,
      title: "Delete Role",
      message: "Are you sure you want to delete this role? This action cannot be undone.",
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`${API_URL}delete-role`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ roleId: id }),
          });
          const data = await response.json();

          if (!response.ok || data.success === false) {
            setToast({ open: true, message: data.message || "Failed to delete role", severity: "error" });
          } else {
            setToast({ open: true, message: "Role deleted successfully", severity: "success" });
            fetchRolesAndPermissions();
          }
        } catch (err) {
          setToast({ open: true, message: "Network error deleting role", severity: "error" });
        }
      },
    });
  };

  const filteredRoles = roles.filter((r) => {
    const term = roleSearch.toLowerCase();
    const friendlyName = getDisplayRoleName(r).toLowerCase();
    return (r.role && r.role.toLowerCase().includes(term)) || friendlyName.includes(term);
  });

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />

      {/* SECTION 1: ROLES & PERMISSIONS MATRIX */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                backgroundColor: "rgba(76, 161, 175, 0.2)",
                border: "1px solid rgba(76, 161, 175, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#4CA1AF",
              }}
            >
              <SecurityIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#FFFFFF">
                Roles & Permissions Matrix
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)", mt: 0.3 }}>
                Granular access control matrix for View, Add, Edit, and Delete actions across all modules.
              </Typography>
            </Box>
          </Box>

          {selectedRole && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveMatrix}
              disabled={saving}
              sx={{
                background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
                color: "#ffffff",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: "0 4px 14px rgba(76, 161, 175, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
                  boxShadow: "0 6px 20px rgba(76, 161, 175, 0.5)",
                },
              }}
            >
              {saving ? "Saving..." : "Save Matrix"}
            </Button>
          )}
        </Box>

        {/* Target Role Dropdown */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel sx={{ color: "rgba(255, 255, 255, 0.7)" }}>Select Target Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Select Target Role"
              sx={{
                borderRadius: 2,
                color: "#FFFFFF",
                background: "rgba(255, 255, 255, 0.05)",
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                "&:hover fieldset": { borderColor: "#4CA1AF" },
                "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
              }}
            >
              <MenuItem value="">
                <em>-- Select Role --</em>
              </MenuItem>
              {roles
                .filter((role) => role.id !== "1" && role.id !== "4")
                .map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {getDisplayRoleName(role)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {selectedRole && (
            <Chip
              label="Editing Active Permissions"
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {loading ? (
          <SectionLoader />
        ) : !selectedRole ? (
          <Box sx={{ textAlign: "center", py: 5, color: "rgba(255, 255, 255, 0.6)" }}>
            <SecurityIcon sx={{ fontSize: 44, opacity: 0.4, mb: 1 }} />
            <Typography variant="body1">
              Please select a role from the dropdown above to view and configure its permissions matrix.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "rgba(76, 161, 175, 0.12)" }}>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700, width: 80 }}>#</TableCell>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700 }}>Module</TableCell>
                  {permissionTypes.map((type) => (
                    <TableCell key={type.key} align="center" sx={{ color: "#4CA1AF", fontWeight: 700 }}>
                      {type.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((perm, index) => (
                  <TableRow
                    key={perm.id}
                    sx={{
                      "&:hover": { background: "rgba(255, 255, 255, 0.04)" },
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                      {perm.module}
                    </TableCell>
                    {permissionTypes.map((type) => (
                      <TableCell key={type.key} align="center">
                        <Checkbox
                          checked={rolePermissions[perm.id]?.[type.key] || false}
                          onChange={handleCheckboxChange(perm.id, type.key, perm.module)}
                          sx={{
                            color: "rgba(255, 255, 255, 0.3)",
                            "&.Mui-checked": {
                              color: "#4CA1AF",
                            },
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* SECTION 2: SYSTEM ROLES MANAGEMENT */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6" fontWeight={700} color="#FFFFFF">
              System Roles
            </Typography>
            <Chip
              label={`${filteredRoles.length} Roles`}
              size="small"
              sx={{
                background: "rgba(76, 161, 175, 0.2)",
                color: "#4CA1AF",
                fontWeight: 600,
                border: "1px solid rgba(76, 161, 175, 0.3)",
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search role name..."
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 240 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  color: "#FFFFFF",
                  background: "rgba(255, 255, 255, 0.05)",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                  "&:hover fieldset": { borderColor: "#4CA1AF" },
                  "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                },
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenRoleModal()}
              sx={{
                background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
                color: "#ffffff",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 0.8,
                boxShadow: "0 4px 14px rgba(76, 161, 175, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
                  boxShadow: "0 6px 20px rgba(76, 161, 175, 0.5)",
                },
              }}
            >
              Add Role
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "rgba(76, 161, 175, 0.12)" }}>
                <TableCell sx={{ color: "#4CA1AF", fontWeight: 700, width: 70 }}>#</TableCell>
                <TableCell sx={{ color: "#4CA1AF", fontWeight: 700 }}>Role Name</TableCell>
                <TableCell align="right" sx={{ color: "#4CA1AF", fontWeight: 700, width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map((r, idx) => (
                <TableRow
                  key={r.id}
                  sx={{
                    "&:hover": { background: "rgba(255, 255, 255, 0.04)" },
                    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                  }}
                >
                  <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}>
                    {idx + 1}
                  </TableCell>
                  <TableCell sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AdminPanelSettingsIcon sx={{ fontSize: 18, color: "#4CA1AF", opacity: 0.8 }} />
                      {getDisplayRoleName(r)}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenRoleModal(r)}
                      sx={{
                        color: "#4CA1AF",
                        mr: 1,
                        "&:hover": { background: "rgba(76, 161, 175, 0.15)" },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRole(r.id)}
                      sx={{
                        color: "#EF4444",
                        "&:hover": { background: "rgba(239, 68, 68, 0.15)" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRoles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ color: "rgba(255, 255, 255, 0.5)", py: 4 }}>
                    No roles found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Role Modal */}
      <Dialog
        open={roleDialogOpen}
        onClose={handleCloseRoleModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: "#1E293B",
            color: "#FFFFFF",
            borderRadius: 3,
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid rgba(255, 255, 255, 0.1)", pb: 2 }}>
          {editingRole ? "Edit Role" : "Add New Role"}
        </DialogTitle>
        <form onSubmit={handleSubmitRole}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              label="Role Name"
              placeholder="e.g. Content Manager"
              type="text"
              fullWidth
              value={roleFormValue}
              onChange={(e) => setRoleFormValue(e.target.value)}
              required
              InputLabelProps={{ sx: { color: "rgba(255, 255, 255, 0.7)" } }}
              InputProps={{
                sx: {
                  color: "#FFFFFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "#4CA1AF" },
                  "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <Button onClick={handleCloseRoleModal} sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
                fontWeight: 600,
                px: 3,
              }}
            >
              {editingRole ? "Save Changes" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default RolesPermissionsTab;
